/**
 * SASADM-0149 — per-product OAuth agent clients + published scope catalog
 * (saas-auth SASATH-0331).
 *
 * SUPER_ADMIN-only methods on `/api/saas-admin/products/{id}/scopes` and
 * `…/agent-clients`. Enveloped `unwrap`, same factory as the other admin
 * clients. `authorizeUrl` / `tokenUrl` are returned by the API — callers
 * must render them verbatim and never compose them.
 */
import { request, unwrap } from '../client';
import type {
  AgentClientResponse,
  ApiResponse,
  CreateAgentClientRequest,
  ProductScopeDto,
} from '../types';

const scopesBase = (productId: number) => `/api/saas-admin/products/${productId}/scopes`;
const clientsBase = (productId: number) => `/api/saas-admin/products/${productId}/agent-clients`;

export const agentClientsApi = {
  async listScopes(productId: number): Promise<ProductScopeDto[]> {
    const res = await request<ApiResponse<ProductScopeDto[]>>('GET', scopesBase(productId));
    return unwrap(res);
  },

  async deleteScope(productId: number, scope: string): Promise<void> {
    await request<ApiResponse<unknown>>(
      'DELETE',
      `${scopesBase(productId)}/${encodeURIComponent(scope)}`,
    );
  },

  async list(productId: number): Promise<AgentClientResponse[]> {
    const res = await request<ApiResponse<AgentClientResponse[]>>('GET', clientsBase(productId));
    return unwrap(res);
  },

  async get(productId: number, clientDbId: number): Promise<AgentClientResponse> {
    const res = await request<ApiResponse<AgentClientResponse>>(
      'GET',
      `${clientsBase(productId)}/${clientDbId}`,
    );
    return unwrap(res);
  },

  async create(productId: number, body: CreateAgentClientRequest): Promise<AgentClientResponse> {
    const res = await request<ApiResponse<AgentClientResponse>>('POST', clientsBase(productId), {
      json: body,
    });
    return unwrap(res);
  },

  async setRedirectUris(
    productId: number,
    clientDbId: number,
    redirectUris: string[],
  ): Promise<AgentClientResponse> {
    const res = await request<ApiResponse<AgentClientResponse>>(
      'PUT',
      `${clientsBase(productId)}/${clientDbId}/redirect-uris`,
      { json: { redirectUris } },
    );
    return unwrap(res);
  },

  async setScopes(
    productId: number,
    clientDbId: number,
    scopes: string[],
  ): Promise<AgentClientResponse> {
    const res = await request<ApiResponse<AgentClientResponse>>(
      'PUT',
      `${clientsBase(productId)}/${clientDbId}/scopes`,
      { json: { scopes } },
    );
    return unwrap(res);
  },

  async disable(productId: number, clientDbId: number): Promise<AgentClientResponse> {
    const res = await request<ApiResponse<AgentClientResponse>>(
      'POST',
      `${clientsBase(productId)}/${clientDbId}/disable`,
    );
    return unwrap(res);
  },

  async enable(productId: number, clientDbId: number): Promise<AgentClientResponse> {
    const res = await request<ApiResponse<AgentClientResponse>>(
      'POST',
      `${clientsBase(productId)}/${clientDbId}/enable`,
    );
    return unwrap(res);
  },
};
